import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';
import { DATA_RANGES, RangeSelector, useTimeRange } from './TimeRange';

// The record of what you've logged, structured the way you logged it: by day, then
// (for food) grouped into meals, and everything else grouped by what it is. The parser
// now tags food with a meal_type, so recent days show real meals; older entries that
// predate that tagging simply fall under "Food" — nothing is lost, it's just flatter.
//
// This is a READ surface. It shows what YOU logged — device/wearable auto-syncs (Fitbit
// steps, sleep, etc.) live in their own cards on Home/Data and would only bury the intent
// here, so they're filtered out.
//
// It's a Data tab now (it used to be a second tab on the Log page, which cost a permanent
// tab bar on the smallest screen we have to review data on the screen meant for capture).
// So it takes the same shared time range as every other Data tab instead of its own
// two-weeks-then-"load earlier" window — one control, remembered across tabs.

// The server caps a page at 500 rows. A wide range on a busy account can hit that, and a
// silently truncated history reads as lost data, so we say so when it happens.
const PAGE_LIMIT = 500;

const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack', 'other'];
const MEAL_META = {
  breakfast: { label: 'Breakfast', emoji: '🍳' },
  lunch: { label: 'Lunch', emoji: '🥗' },
  dinner: { label: 'Dinner', emoji: '🍽️' },
  snack: { label: 'Snack', emoji: '🍎' },
  other: { label: 'Food', emoji: '🍴' },
};

const SECTION_ORDER = [
  'exercise', 'supplements', 'hydration', 'sleep', 'biometric',
  'mental_health', 'therapy', 'medical', 'environmental', 'social', 'other',
];
const SECTION_META = {
  exercise: { label: 'Exercise', emoji: '🏃' },
  supplements: { label: 'Supplements', emoji: '💊' },
  hydration: { label: 'Hydration', emoji: '💧' },
  sleep: { label: 'Sleep', emoji: '😴' },
  biometric: { label: 'Body & vitals', emoji: '📏' },
  mental_health: { label: 'Mind & mood', emoji: '🧠' },
  therapy: { label: 'Therapy', emoji: '🌿' },
  medical: { label: 'Medical', emoji: '🩺' },
  environmental: { label: 'Environment', emoji: '🌤️' },
  social: { label: 'Social', emoji: '👥' },
  other: { label: 'Other', emoji: '•' },
};

// Parse a YYYY-MM-DD as a LOCAL calendar day (never `new Date(str)`, which is UTC and can
// shift the day across the timezone boundary — the same trap localToday() avoids).
const parseLocal = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const shiftDays = (s, delta) => {
  const d = parseLocal(s);
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
};

const dayHeading = (dateStr) => {
  const today = localToday();
  if (dateStr === today) return 'Today';
  if (dateStr === shiftDays(today, -1)) return 'Yesterday';
  const d = parseLocal(dateStr);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const rest = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${weekday}, ${rest}`;
};

const humanize = (t) => (t || '').replace(/_/g, ' ');

const formatTime = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
};

const caloriesOf = (e) => {
  if (e.calories != null) return e.calories;
  if (e.value != null && e.value_unit === 'kcal') return e.value;
  // Older entries (logged before calories were promoted to the column) can still carry the
  // number in the JSONB blob — read it so their totals aren't silently zero.
  if (e.data && e.data.calories != null) return e.data.calories;
  return null;
};

// The right-hand detail for a non-food line: value + unit, then duration, then calories.
const detailOf = (e) => {
  const parts = [];
  if (e.value != null) parts.push(`${round(e.value)}${e.value_unit ? ' ' + e.value_unit : ''}`);
  if (e.duration_minutes != null) parts.push(`${round(e.duration_minutes)} min`);
  if (e.calories != null && e.value_unit !== 'kcal') parts.push(`${round(e.calories)} cal`);
  return parts.join(' · ');
};

const round = (n) => (Math.round(n * 10) / 10);

const sectionKey = (slug) => {
  if (slug.startsWith('nutrition.supplement')) return 'supplements';
  if (slug.startsWith('nutrition.hydration')) return 'hydration';
  if (slug.startsWith('exercise')) return 'exercise';
  return slug.split('.')[0];
};

// Turn a flat, date-desc entry list into [{ date, meals:[{key,items,calories}], sections:[{key,items}], calories }].
const groupByDay = (entries) => {
  const byDate = new Map();
  for (const e of entries) {
    if (!byDate.has(e.entry_date)) byDate.set(e.entry_date, []);
    byDate.get(e.entry_date).push(e);
  }
  const days = [];
  for (const [date, dayEntries] of byDate) {
    const mealBuckets = new Map();
    const sectionBuckets = new Map();
    for (const e of dayEntries) {
      if (e.category_slug.startsWith('nutrition.food')) {
        const key = MEAL_META[e.data?.meal_type] ? e.data.meal_type : 'other';
        if (!mealBuckets.has(key)) mealBuckets.set(key, []);
        mealBuckets.get(key).push(e);
      } else {
        const key = SECTION_META[sectionKey(e.category_slug)] ? sectionKey(e.category_slug) : 'other';
        if (!sectionBuckets.has(key)) sectionBuckets.set(key, []);
        sectionBuckets.get(key).push(e);
      }
    }
    const sortItems = (a, b) => (a.entry_time || '99:99').localeCompare(b.entry_time || '99:99');
    const meals = MEAL_ORDER
      .filter((k) => mealBuckets.has(k))
      .map((k) => {
        const items = mealBuckets.get(k).sort(sortItems);
        const calories = items.reduce((s, e) => s + (caloriesOf(e) || 0), 0);
        return { key: k, items, calories };
      });
    const sections = SECTION_ORDER
      .filter((k) => sectionBuckets.has(k))
      .map((k) => ({ key: k, items: sectionBuckets.get(k).sort(sortItems) }));
    const calories = meals.reduce((s, m) => s + m.calories, 0);
    days.push({ date, meals, sections, calories });
  }
  // byDate preserves the server's date-desc order because entries arrive date-desc.
  return days;
};

const LogHistory = ({ refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);

  const { selectorProps } = useTimeRange();
  const { rangeKey, customStart, customEnd } = selectorProps;

  // The range picks the fetch window directly (rather than over-fetching and filtering
  // client-side like the chart tabs do) — history rows are far heavier than a datapoint,
  // and the endpoint already takes a date window.
  const [startParam, endParam] = useMemo(() => {
    const today = localToday();
    if (rangeKey === 'ALL') return [null, today];
    if (rangeKey === 'CUSTOM') return [customStart || null, customEnd || today];
    const days = DATA_RANGES.find((r) => r.key === rangeKey)?.days;
    return [days ? shiftDays(today, -days) : null, today];
  }, [rangeKey, customStart, customEnd]);

  const fetchWindow = useCallback(async () => {
    setLoading(true);
    try {
      const params = { end_date: endParam, limit: PAGE_LIMIT };
      if (startParam) params.start_date = startParam;
      const res = await apiClient.get('/api/health/entries', { params });
      const raw = res.data || [];
      setTruncated(raw.length >= PAGE_LIMIT);
      setEntries(raw.filter((e) => e.source !== 'fitbit' && e.source !== 'device'));
    } catch (err) {
      console.error('Error loading log history:', err);
    } finally {
      setLoading(false);
    }
  }, [startParam, endParam]);

  useEffect(() => {
    fetchWindow();
  }, [fetchWindow, refreshKey]);

  // The range control renders in every state — loading, empty, and full. An empty window
  // is the state where you MOST need to widen the range, so hiding the selector there
  // (the old code hid everything but a "look further back" link) stranded you.
  const selector = (
    <div className="mb-5">
      <RangeSelector {...selectorProps} />
    </div>
  );

  if (loading) {
    return (
      <div>
        {selector}
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-4">
              <div className="animate-pulse space-y-3">
                <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-16 bg-gray-100 dark:bg-gray-800 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const days = groupByDay(entries);

  if (days.length === 0) {
    return (
      <div>
        {selector}
        <div className="rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-10 text-center">
          <div className="text-3xl mb-3">📖</div>
          <p className="text-base font-medium text-gray-700 dark:text-gray-200">Nothing logged in this range</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Widen the range above, or log something on the Log tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {selector}
      {truncated && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Showing the {PAGE_LIMIT} most recent entries in this range — narrow the range to
          see further back.
        </p>
      )}
      {days.map((day) => (
        <div
          key={day.date}
          className="rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card overflow-hidden"
        >
          {/* Day header — relative name on the left, the day's food-calorie total on the
              right so the number you glance for is right where the eye lands. */}
          <div className="flex items-baseline justify-between gap-3 px-4 py-3 border-b border-surface-border dark:border-surface-dark-border">
            <h3 className="text-base font-bold text-gray-900 dark:text-white">{dayHeading(day.date)}</h3>
            {day.calories > 0 && (
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                {round(day.calories)} cal
              </span>
            )}
          </div>

          <div className="divide-y divide-surface-border dark:divide-surface-dark-border">
            {day.meals.map((meal) => (
              <div key={meal.key} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {MEAL_META[meal.key].emoji} {MEAL_META[meal.key].label}
                  </span>
                  {meal.calories > 0 && (
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {round(meal.calories)} cal
                    </span>
                  )}
                </div>
                <ul className="space-y-1">
                  {meal.items.map((e) => (
                    <li key={e.id} className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-gray-700 dark:text-gray-200 capitalize truncate">
                        {humanize(e.entry_type)}
                        {e.entry_time && (
                          <span className="ml-2 text-sm text-gray-400 dark:text-gray-500 font-mono normal-case">
                            {formatTime(e.entry_time)}
                          </span>
                        )}
                      </span>
                      {caloriesOf(e) != null && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                          {round(caloriesOf(e))} cal
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {day.sections.map((section) => (
              <div key={section.key} className="px-4 py-3">
                <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
                  {SECTION_META[section.key].emoji} {SECTION_META[section.key].label}
                </div>
                <ul className="space-y-1">
                  {section.items.map((e) => (
                    <li key={e.id} className="flex items-baseline justify-between gap-3">
                      <span className="text-sm text-gray-700 dark:text-gray-200 capitalize truncate">
                        {humanize(e.entry_type)}
                        {e.entry_time && (
                          <span className="ml-2 text-sm text-gray-400 dark:text-gray-500 font-mono normal-case">
                            {formatTime(e.entry_time)}
                          </span>
                        )}
                      </span>
                      {detailOf(e) && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                          {detailOf(e)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LogHistory;
